import React, { useCallback, useMemo, useState, useContext, useEffect } from 'react';
import dayjs from 'dayjs';
import { Box, Chip, Divider, Paper, Stack, Tooltip, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import OpacityIcon from '@mui/icons-material/Opacity';
import Co2Icon from '@mui/icons-material/Co2';
import LaunchIcon from '@mui/icons-material/Launch';
import SettingsIcon from '@mui/icons-material/Settings';
import { StorageHealthConfig } from '../../../Types/StorageHealth';
import { getModeVisual } from '../../../Helpers/modeVisuals';
import UserContext from '../../../User/UserSettings/UserContext';
import API from '../../../api2';
import AlarmStatus from './AlarmStatus';

export interface IoTMainData {
    plenumTemp?: number | string | null;
    plenumHumid?: number | string | null;
    co2Level?: number | string | null;
    outsideTemp?: number | string | null;
    [key: string]: unknown;
}

export interface IoTCurrentMode {
    mode?: string | null;
}

export interface IoTAlarmData {
    alarms?: string[];
}

export interface IoTClientSummaryProps {
    config?: StorageHealthConfig;
    mainData?: IoTMainData | null;
    pgmData?: any | null;
    currentMode?: IoTCurrentMode | null;
    timeStamp?: string | null;
    alarmData?: IoTAlarmData | null;
}

const formatValue = (value: number | string | null | undefined, unit?: string) => {
    if (value === null || value === undefined) return '—';

    const numeric = typeof value === 'string' ? Number(value) : value;
    if (typeof numeric === 'number' && !Number.isNaN(numeric)) {
        return `${numeric.toFixed(1)}${unit ?? ''}`;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
        return `${value}${unit ?? ''}`;
    }

    return '—';
};

const toTitleCase = (value: string | null | undefined) => {
    if (!value) return 'Unknown';
    return value
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const IoTClientSummary: React.FC<IoTClientSummaryProps> = ({ config, mainData, pgmData, currentMode, timeStamp, alarmData }) => {
    const theme = useTheme();
    const { user, setUser } = useContext(UserContext) || { user: null, setUser: () => { } };
    const [openSettings, setOpenSettings] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

    const availableOptions = [
        { key: 'plenumTemp', label: 'Plenum Temperature' },
        { key: 'plenumHumid', label: 'Plenum Humidity' },
        { key: 'co2Level', label: 'CO₂ Level' },
        { key: 'outsideTemp', label: 'Outside Temperature' },
        { key: 'plenumTempSetpoint', label: 'Set Point' },
    ];

    useEffect(() => {
        if (user?.panelSettings) {
            setSelectedOptions(user.panelSettings);
        } else {
            setSelectedOptions(['plenumTemp', 'plenumHumid', 'co2Level']);
        }
    }, [user]);

    const handleSaveSettings = async () => {
        if (selectedOptions.length < 2 || selectedOptions.length > 4) {
            alert("Please select between 2 and 4 options.");
            return;
        }
        
        try {
            const response = await API.request('user/panel-settings', { email: user.email, settings: selectedOptions }, 'patch');
            if (!response.error) {
                setUser({ ...user, panelSettings: response.data });
                setOpenSettings(false);
            } else {
                console.error(response.message);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleToggleOption = (key: string) => {
        setSelectedOptions(prev => {
            if (prev.includes(key)) {
                return prev.filter(k => k !== key);
            } else {
                if (prev.length >= 4) return prev;
                return [...prev, key];
            }
        });
    };

    const modeLabel = toTitleCase(currentMode?.mode ?? null);
    const { chipColor, accentColor } = useMemo(() => getModeVisual(currentMode?.mode ?? null), [currentMode?.mode]);
    const parsedTimestamp = timeStamp ? dayjs(timeStamp) : null;
    const timestampDisplay = parsedTimestamp?.isValid() ? parsedTimestamp.format('MMM D, YYYY • h:mm A') : null;
    const timestampTooltip = parsedTimestamp?.isValid() ? parsedTimestamp.format('MMMM D, YYYY h:mm A') : null;

    const cardVisuals = useMemo(() => {
        const paletteKey = chipColor && chipColor !== 'default' ? chipColor : null;
        const paletteColor = paletteKey ? (theme.palette as any)[paletteKey] : null;
        const baseColor = accentColor || paletteColor?.main || null;

        if (baseColor) {
            return {
                backgroundColor: alpha(baseColor, 0.12),
                borderColor: alpha(baseColor, 0.5),
                hoverShadow: `0 0 0 2px ${alpha(baseColor, 0.18)}, 0 10px 24px -14px ${alpha(baseColor, 0.42)}`,
                focusShadow: `0 0 0 2px ${alpha(baseColor, 0.55)}`,
                activeShadow: `0 0 0 2px ${alpha(baseColor, 0.28)}`
            };
        }

        const primaryMain = theme.palette.primary.main;
        return {
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
            hoverShadow: `0 0 0 2px ${alpha(primaryMain, 0.16)}, 0 8px 20px -12px ${alpha(primaryMain, 0.38)}`,
            focusShadow: `0 0 0 2px ${alpha(primaryMain, 0.5)}`,
            activeShadow: `0 0 0 2px ${alpha(primaryMain, 0.28)}`
        };
    }, [accentColor, chipColor, theme]);

    const handleCardClick = useCallback(() => {
        if (typeof window === 'undefined') return;

        const targetUrl = config?.iotClientId
            ? `https://agristor-companies.com/storage-app/devices/${config.iotClientId}/settings`
            : '/';
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }, [config?.iotClientId]);

    const renderCard = (key: string) => {
        switch (key) {
            case 'plenumTemp':
                return (
                    <Stack direction="row" spacing={2} alignItems="center" key={key}>
                        <DeviceThermostatIcon color="action" />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Plenum temperature
                            </Typography>
                            <Typography variant="h6">{formatValue(mainData?.plenumTemp, '°F')}</Typography>
                        </Box>
                    </Stack>
                );
            case 'plenumHumid':
                return (
                    <Stack direction="row" spacing={2} alignItems="center" key={key}>
                        <OpacityIcon color="action" />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Plenum humidity
                            </Typography>
                            <Typography variant="h6">{formatValue(mainData?.plenumHumid, '%')}</Typography>
                        </Box>
                    </Stack>
                );
            case 'co2Level':
                const currentCo2 = typeof mainData?.co2Level === 'string' ? parseFloat(mainData.co2Level) : mainData?.co2Level;
                const targetCo2 = config?.targetCo2;
                const isHighCo2 = typeof currentCo2 === 'number' && typeof targetCo2 === 'number' && !isNaN(currentCo2) && !isNaN(targetCo2) && currentCo2 > targetCo2;

                return (
                    <Stack direction="row" spacing={2} alignItems="center" key={key}>
                        <Co2Icon color={isHighCo2 ? "error" : "action"} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                CO₂ level
                            </Typography>
                            <Typography variant="h6" color={isHighCo2 ? "error.main" : "text.primary"}>
                                {formatValue(mainData?.co2Level, ' ppm')}
                            </Typography>
                        </Box>
                    </Stack>
                );
            case 'outsideTemp':
                return (
                    <Stack direction="row" spacing={2} alignItems="center" key={key}>
                        <DeviceThermostatIcon color="action" />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Outside temperature
                            </Typography>
                            <Typography variant="h6">{formatValue(mainData?.outsideTemp, '°F')}</Typography>
                        </Box>
                    </Stack>
                );
            case 'plenumTempSetpoint':
                return (
                    <Stack direction="row" spacing={2} alignItems="center" key={key}>
                        <DeviceThermostatIcon color="action" />
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Set Point
                            </Typography>
                            <Typography variant="h6">{formatValue(pgmData?.plenumTempSetpoint, '°F')}</Typography>
                        </Box>
                    </Stack>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Paper
                variant="outlined"
                onClick={handleCardClick}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleCardClick();
                    }
                }}
                role="button"
                tabIndex={0}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: theme.transitions.create('box-shadow', {
                        duration: theme.transitions.duration.shorter,
                    }),
                    boxShadow: 'none',
                    borderColor: cardVisuals.borderColor,
                    backgroundColor: cardVisuals.backgroundColor,
                    '&:hover': {
                        boxShadow: cardVisuals.hoverShadow,
                        borderColor: cardVisuals.borderColor,
                    },
                    '&:focus-visible': {
                        boxShadow: cardVisuals.focusShadow,
                        outline: 'none',
                        borderColor: cardVisuals.borderColor,
                    },
                    '&:active': {
                        boxShadow: cardVisuals.activeShadow,
                        borderColor: cardVisuals.borderColor,
                    }
                }}
            >
                <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" rowGap={1} columnGap={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={600}>
                                Live panel status
                            </Typography>
                            <Tooltip title="Opens full panel settings in a new browser tab">
                                <LaunchIcon fontSize="small" color="action" />
                            </Tooltip>
                            <Tooltip title="Configure panel display">
                                <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenSettings(true);
                                    }}
                                >
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <AlarmStatus alarms={alarmData?.alarms} />
                            <Chip label={modeLabel} color={chipColor} variant={chipColor === 'default' ? 'outlined' : 'filled'} />
                        </Stack>
                    </Box>

                    <Box
                        display="grid"
                        gap={2}
                        gridTemplateColumns={{ 
                            xs: '1fr', 
                            sm: `repeat(${Math.max(1, selectedOptions.length)}, minmax(0, 1fr))` 
                        }}
                    >
                        {selectedOptions.map(key => renderCard(key))}
                    </Box>

                    <Divider />
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={0.5} columnGap={2}>
                        <Typography variant="caption" color="text.secondary">
                            Click anywhere on this card to open the Agri-Star dashboard.
                        </Typography>
                        {timestampDisplay ? (
                            <Tooltip title={timestampTooltip ?? ''} arrow disableHoverListener={!timestampTooltip}>
                                <Typography variant="caption" color="text.secondary">
                                    {timestampDisplay}
                                </Typography>
                            </Tooltip>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                Updated time unavailable
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Paper>

            <Dialog open={openSettings} onClose={() => setOpenSettings(false)}>
                <DialogTitle>Configure Panel Display</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select 2-4 items to display on the panel status card.
                    </Typography>
                    <FormGroup>
                        {availableOptions.map((option) => (
                            <FormControlLabel
                                key={option.key}
                                control={
                                    <Checkbox
                                        checked={selectedOptions.includes(option.key)}
                                        onChange={() => handleToggleOption(option.key)}
                                        disabled={!selectedOptions.includes(option.key) && selectedOptions.length >= 4}
                                    />
                                }
                                label={option.label}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSettings(false)}>Cancel</Button>
                    <Button onClick={handleSaveSettings} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default IoTClientSummary;
